// Destinations in the wider StudyPages platform that the top bar links out to.
//
// Everything here resolves from configured base URLs rather than hardcoded hosts, so the links
// follow SP Data when it moves to its own domain (ENG-7346 / ENG-7348) without a code change.

const trimTrailingSlash = (url: string) => url.replace(/\/+$/, '');

const WORKSPACE_BASE_URL = trimTrailingSlash(import.meta.env.VITE_APP_STUDYPAGES_BASE_URL ?? '');
/**
 * Where SP Data itself is served: the configured base, or the current origin when none is set. A
 * base that trims to nothing (empty, blank, or only `/`) falls back too, since a bare `/form/:id`
 * copied into an email goes nowhere.
 */
export const DATA_BASE_URL =
  trimTrailingSlash((import.meta.env.VITE_APP_FRONTEND_BASE_URL ?? '').trim()) ||
  window.location.origin;

export const platformLinks = {
  knowledgeBase: `${WORKSPACE_BASE_URL}/support/knowledge_base/`,
  help: `${WORKSPACE_BASE_URL}/platform/help`,
  workspaceHome: `${WORKSPACE_BASE_URL}/dashboard/`,
  // Eventually this will be the SP Data dashboard, showing studies and other data-related information.
  spDataHome: `${DATA_BASE_URL}/`
};

export interface MobileAppModel {
  id: 'android' | 'ios';
  /** Tile label, and what the QR dialog is titled for. */
  name: string;
  /** Where the QR code points, and what the "open" action follows. */
  url: string;
  /** Name of the store, used in the dialog's copy. */
  store: string;
  icon: string;
}

/**
 * The SP Data mobile apps. These are public store listings rather than deployment-specific
 * hosts, so unlike the links above they don't vary by environment.
 */
export const mobileApps: MobileAppModel[] = [
  {
    id: 'android',
    name: 'Android',
    url: 'https://play.google.com/store/apps/details?id=com.teamscopeapp',
    store: 'Google Play',
    icon: 'android'
  },
  {
    id: 'ios',
    name: 'iPhone & iPad',
    url: 'https://apps.apple.com/us/app/studypages-data-teamscope/id950837428',
    store: 'App Store',
    icon: 'apple'
  }
];

/**
 * The PUMP companion apps. Only offered on PUMP studies — see `customDataDictionary` on the
 * selected study's flags.
 */
export const pumpMobileApps: MobileAppModel[] = [
  {
    id: 'android',
    name: 'Android',
    url: 'https://play.google.com/store/apps/details?id=com.studypages.pump',
    store: 'Google Play',
    icon: 'android'
  },
  {
    id: 'ios',
    name: 'iPhone & iPad',
    url: 'https://apps.apple.com/us/app/pump-mobile/id6742456875',
    store: 'App Store',
    icon: 'apple'
  }
];

export interface PlatformAppModel {
  id: string;
  name: string;
  url: string;
  /** Bundled logo. Apps without one fall back to an initials tile. */
  logo?: string;
  /** Marks the app the user is currently in, so the switcher can indicate it. */
  current: boolean;
}

/**
 * The apps the platform switcher offers.
 *
 * The workspace builds the equivalent list server-side from `/nav/user_apps/`, which SP Data
 * cannot call yet — that endpoint is session-cookie authenticated and same-origin only. Until
 * it is reachable cross-origin this mirrors it from configuration, which preserves the
 * "resolves from configured URLs" behaviour the endpoint exists to provide.
 *
 * TODO(ENG-7346): source this from `/nav/user_apps/` once it accepts cross-origin token auth,
 * so per-user app visibility (SP Pocket, Pump-only users) comes from the backend.
 */
export const getPlatformApps = (dataAppLogo?: string, spAppLogo?: string): PlatformAppModel[] => [
  {
    id: 'sp-workspace',
    name: 'SP Workspace',
    url: WORKSPACE_BASE_URL,
    logo: spAppLogo,
    current: false
  },
  {
    id: 'sp-data',
    name: 'SP Data',
    url: DATA_BASE_URL,
    logo: dataAppLogo,
    current: true
  }
];
